/* eslint global-require: 0 */
/* eslint import/no-dynamic-require: 0 */
/* eslint-disable no-lonely-if */
import Vue from 'vue';
import Swiper from 'swiper';
// eslint-disable-next-line import/extensions
import { Navigation, Pagination } from 'swiper/modules';
import StickyScroller from '../../scripts/lib/sticky-scroller';
import { trackImageView } from '../tracking/trackingLayer';
import vspButton from '../vsp-button/vsp-button.vue.component';
import { calculateVisiblePx, calculateVisibleRowPercentage } from './image-visibility';

const bilderAnsichtThumbnails = () => import('../bilder-ansicht-thumbnails/bilder-ansicht-thumbnails.vue.component');

export default {
  template: '#image-main-bildansicht-template',

  data: () => ({
    pageLoaded: false,
    pageResized: false,
    swiperIndex: 0,
    activeThumbnail: null,
    imageTrackingSent: [],
    visibleImages: [],
    timers: [],
  }),

  props: {
    productId: {
      type: Number,
      required: true,
    },
    images: {
      type: Array,
      required: true,
    },
    active: {
      type: Object,
    },
    previewImage: {
      type: Object,
      required: false,
      default: null,
    },
    farbId: {
      type: String,
      required: false,
    },
    modelmaszeInfo: {
      type: Boolean,
      required: false,
    },
  },

  components: {
    'bilder-ansicht-thumbnails': bilderAnsichtThumbnails,
    'vsp-button': vspButton,
  },

  watch: {
    images() {
      Vue.nextTick(() => {
        this.initializeSwiperSlider();
        this.imageObserver();
      });
    },
  },

  mounted() {
    this.initializeSwiperSlider();
    this.imageObserver();

    const stickyTestContainer = document.querySelector('.ents-pds-summary__wrapper--sticky');
    // eslint-disable-next-line no-new
    new StickyScroller(stickyTestContainer); // NOSONAR

    this.$parent.$on('bewerten:vue:image:zoom:reset-and-open', (triggeredFromProductDetails) => {
      this.openZoomOverlay('reset', triggeredFromProductDetails);
    });

    window.addEventListener('load', () => this.setPageLoaded());
  },

  computed: {
    showPreviewImage() {
      return !!(this.previewImage
        && this.images[0].fallbackStandardRedesignUrl !== this.previewImage.fallbackStandardRedesignUrl
      );
    },
  },

  methods: {
    buildSrcSet(ansicht) {
      return [
        [ansicht.thumbnailUrl, '144w'],
        [ansicht.thumbnailRetinaUrl, '288w'],
        [ansicht.standardUrl, '435w'],
        [ansicht.standardRedesignUrl, '680w'],
        [ansicht.standardRetinaUrl, '870w'],
        [ansicht.zoomUrl, '1122w'],
        [ansicht.standardRedesignRetinaUrl, '1360w'],
        [ansicht.zoomRetinaUrl, '2244w'],
      ]
        // remove non existent urls
        .filter(([src]) => !!src)
        // build srcset string
        .map(([src, size]) => `${src} ${size}`)
        .join(',\n');
    },

    setPageLoaded() {
      this.pageLoaded = true;
    },

    openZoomOverlay(index, triggeredFromProductDetails) {
      import('../touchzoomCanvas/touchzoomCanvas').then(({ default: touchzoomCanvas }) => {
        if (this.pageLoaded) {
          touchzoomCanvas.initWithIndex(this.images, index, this.modelmaszeInfo, triggeredFromProductDetails);
        }
      });
    },

    initializeSwiperSlider() {
      this.pageResized = false;
      if (this.swiper) {
        this.swiper.destroy();
      }
      /*  eslint-disable-next-line no-new */
      this.swiper = new Swiper(this.$refs.swiper, { // NOSONAR
        modules: [Navigation, Pagination],
        enabled: this.images.length > 1,
        initialSlide: this.swiperIndex,
        autoplay: false,
        loop: true,
        spaceBetween: 10,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        navigation: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        },
        breakpoints: {
          560: {
            slidesPerView: 1.6,
            centeredSlides: true,
            loop: true,
          },
          // 710 and 860 are not breakpoints we usually use in our library.
          // When the slider only gets wider but not higher anymore, it would make the images float away from each other.
          // So we need the breakpoints in this case to show "more" of the cut-off images and preserve the spacings between them.
          710: {
            slidesPerView: 2,
            centeredSlides: true,
            loop: true,
          },
          860: {
            slidesPerView: 2.2,
            centeredSlides: true,
            loop: true,
          },
          960: {
            loop: false,
          },
        },
        on: {
          slideChange: () => {
            if (this.swiper && !this.swiper.destroyed) {
              if (typeof this.swiper.realIndex === 'undefined') {
                this.swiperIndex = 0;
              } else {
                this.swiperIndex = this.swiper.realIndex;
              }
            }
          },
          beforeResize: () => {
            // reinit swiper on page resize so all slides are properly shown
            // solves bug with disappearing slide to the left of the active slide
            //
            // 'pageResized' flag is necessary because Swiper's resize handler
            // is always triggered once after initialization, but we only want
            // to reinit Swiper on actual page resizes
            if (this.pageResized) {
              this.initializeSwiperSlider();
            } else {
              this.pageResized = true;
            }
          },
        },
      });
    },

    imageObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.imageTrackingSent = [];
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      };

      const desktopImages = document.querySelectorAll('.ents-bilder-ansicht__desktop .ents-bilder-ansicht__entry');
      const mobileImages = document.querySelectorAll('.ents-bilder-ansicht__mobile .swiper-slide');

      this.observer = new IntersectionObserver(() => {
        this.compareDesktopImageVisibility(desktopImages);
        this.compareMobileImageVisibility(mobileImages);
      }, options);

      Array.from(desktopImages)
        .concat(Array.from(mobileImages))
        .forEach((el) => {
          const imageIndex = el.getAttribute('data-ents-bilder-ansicht-index') || el.getAttribute('data-swiper-slide-index');
          this.imageTrackingSent[imageIndex] = false;
          this.observer.observe(el);
        });
    },

    compareDesktopImageVisibility(els) {
      const imageCounter = els.length;

      let max = 0;

      // eslint-disable-next-line no-restricted-syntax
      for (const el of Array.from(els)) {
        if (el.offsetHeight === 0) {
          // important, otherwise visiblePercentage will be "infinity", which is > 40%
          return;
        }
        const elementIndex = el.getAttribute('data-ents-bilder-ansicht-index');

        // calculate number of visible pixels (vertically, i.e. rows)
        const visiblePx = calculateVisiblePx(true, el);

        // convert visible pixels into percentage
        const visiblePercentage = calculateVisibleRowPercentage(visiblePx, el);

        // send tracking for picture that is visible more than 40% for over 150ms (if tracking wasn't already sent)
        this.updateVisibleAndSendTracking(visiblePercentage, 40, elementIndex, el, imageCounter);

        if (visiblePx > max) {
          max = visiblePx;
          this.activeThumbnail = parseInt(elementIndex, 10);
        }
      }
    },

    compareMobileImageVisibility(els) {
      const imageCounter = els.length;

      // eslint-disable-next-line no-restricted-syntax
      for (const el of Array.from(els)) {
        if (el.offsetHeight === 0) {
          return;
        }
        const elementIndex = el.getAttribute('data-swiper-slide-index');

        // calculate number of visible pixels (horizontally, i.e. columns)
        const visiblePx = calculateVisiblePx(false, el);

        // convert visible pixels into percentage
        const visiblePercentage = calculateVisibleRowPercentage(visiblePx, el);

        // send tracking for picture that is visible more than 50% for over 150ms (if tracking wasn't already sent)
        this.updateVisibleAndSendTracking(visiblePercentage, 50, elementIndex, el, imageCounter);
      }
    },

    updateVisibleAndSendTracking(visiblePercentage, visibleThreshold, elementIndex, el, imageCounter) {
      if (visiblePercentage >= visibleThreshold && !this.imageTrackingSent[elementIndex]) {
        this.visibleImages[elementIndex] = true;
        // cancel tracking event if there is a new one within 150ms
        if (this.timers[elementIndex]) {
          clearTimeout(this.timers[elementIndex]);
        }
        this.timers[elementIndex] = setTimeout(() => {
          if (this.visibleImages[elementIndex]) {
            this.sendTracking(el, elementIndex, imageCounter);
          }
        }, 150);
      } else {
        this.visibleImages[elementIndex] = false;
      }
    },

    sendTracking(el, elIndex, imageCounter) {
      this.imageTrackingSent[elIndex] = true;
      trackImageView('standard', parseInt(elIndex, 10), imageCounter, this.modelmaszeInfo !== undefined, false);
    },

    openVideo(index) {
      if (this.images[index].videoUrls) {
        const videoUrls = this.images[index].videoUrls;
        const poster = this.images[index].standardUrl;

        const layer = document.querySelector('bewerten-video-layer');
        if (layer != null) {
          layer.dispatchEvent(new CustomEvent(
            'bewe:ents:videoLayerOpen',
            {
              detail: {
                videoUrls,
                poster,
                farbId: this.farbId,
              },
            },
          ));
        }
      }
    },
  },
};
